import type { CatalogDraft, CatalogSnapshot, DraftDetail, Subject } from '@lastrouter/contracts';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  Field,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@lastrouter/ui';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Check, X } from '@lastrouter/ui';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api, ApiError } from '../../api/client.js';
import { errorMessage, useApiMutation, useApiQuery } from '../../api/hooks.js';
import { useSession } from '../../app/session.js';
import { linkClass, PageHeader, QueryState, StatusBadge } from '../../components/common.js';
import { draftTone } from './index.js';
import { SubjectEditor } from './subject-form.js';

type Decision = { subject_id: string; papel: 'negocio' | 'tecnico'; decision: 'aprovado' | 'recusado' };

function DecisionDialog({ draftId, d, subjectName, onClose }: { draftId: string; d: Decision; subjectName: string; onClose: () => void }) {
  const { t } = useTranslation();
  const approve = useApiMutation('approveDraft', ['draft', 'drafts', 'overview']);
  const [comment, setComment] = useState('');
  function submit(e: FormEvent) {
    e.preventDefault();
    approve.mutate(
      { params: { id: draftId }, body: { ...d, comment: comment.trim() || null } },
      { onSuccess: onClose, onError: (err) => toast.error(errorMessage(t, err)) },
    );
  }
  const title = t('context.editor.decisionTitle', {
    decision: d.decision === 'aprovado' ? t('context.editor.approve') : t('context.editor.refuse'),
    papel: d.papel === 'negocio' ? t('context.editor.papelNegocio') : t('context.editor.papelTecnico'),
    subject: subjectName,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={title} closeLabel={t('common.close')}>
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <Field id="comentario-aprovacao" label={`${t('context.editor.comment')} (${t('common.optional')})`}>
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} maxLength={2000} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant={d.decision === 'aprovado' ? 'primary' : 'danger'} disabled={approve.isPending}>
              {d.decision === 'aprovado' ? t('context.editor.approve') : t('context.editor.refuse')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalsCard({ detail, onDecide }: { detail: DraftDetail; onDecide: (d: Decision) => void }) {
  const { t } = useTranslation();
  const { draft, status, can_approve } = detail;
  const name = (id: string) => draft.snapshot.subjects.find((s) => s.id === id)?.nome ?? id;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('context.editor.approvals')}</CardTitle>
        <CardDescription>{t('context.editor.approvalsHelp')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {status.ready ? <Alert tone="success" title={t('context.editor.ready')} /> : null}
        {status.refused ? <Alert tone="danger" title={t('context.editor.refusedState')} /> : null}
        {draft.changed_subjects.map((sid) => (
          <div key={sid} className="flex flex-col gap-2">
            <p className="font-medium">{name(sid)}</p>
            <ul className="flex flex-col gap-1.5">
              {(['negocio', 'tecnico'] as const).map((papel) => {
                const given = draft.approvals.find((a) => a.subject_id === sid && a.papel === papel);
                const pending = status.pending.find((p) => p.subject_id === sid && p.papel === papel);
                const mine = can_approve.some((c) => c.subject_id === sid && c.papel === papel);
                return (
                  <li key={papel} className="flex flex-wrap items-center gap-2">
                    <span className="w-20 text-fg-secondary">{papel === 'negocio' ? t('context.business') : t('context.technical')}</span>
                    {given?.decision === 'aprovado' ? (
                      <StatusBadge tone="success">{t('context.editor.approvedBy', { by: given.by })}</StatusBadge>
                    ) : given?.decision === 'recusado' ? (
                      <StatusBadge tone="danger">{t('context.editor.refusedBy', { by: given.by })}</StatusBadge>
                    ) : (
                      <StatusBadge tone="warning">{t('context.editor.pending', { owner: pending?.owner ?? '' })}</StatusBadge>
                    )}
                    {given?.comment ? <span className="text-[length:var(--lr-text-sm)] text-fg-secondary">"{given.comment}"</span> : null}
                    {mine ? (
                      <span className="flex gap-1">
                        <Button size="sm" variant="primary" onClick={() => onDecide({ subject_id: sid, papel, decision: 'aprovado' })}>
                          <Check />
                          {t('context.editor.approve')}
                        </Button>
                        <Button size="sm" onClick={() => onDecide({ subject_id: sid, papel, decision: 'recusado' })}>
                          <X />
                          {t('context.editor.refuse')}
                        </Button>
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {draft.changed_subjects.length === 0 ? <p className="text-fg-secondary">{t('context.editor.noChanges')}</p> : null}
      </CardContent>
    </Card>
  );
}

/** Confere o catálogo inteiro no serviço enquanto a pessoa edita (meio segundo depois da última mudança). */
function useLiveValidation(snapshot: CatalogSnapshot | null) {
  const [state, setState] = useState<{ pending: boolean; ok: boolean; errors: string[] }>({ pending: false, ok: true, errors: [] });
  const seq = useRef(0);
  useEffect(() => {
    if (!snapshot) return;
    const my = ++seq.current;
    setState((s) => ({ ...s, pending: true }));
    const timer = setTimeout(() => {
      api('validateCatalog', { body: { snapshot } })
        .then((r) => my === seq.current && setState({ pending: false, ok: r.ok, errors: r.errors }))
        .catch(() => my === seq.current && setState((s) => ({ ...s, pending: false })));
    }, 500);
    return () => clearTimeout(timer);
  }, [snapshot]);
  return state;
}

function Editor({ detail }: { detail: DraftDetail }) {
  const { t } = useTranslation();
  const session = useSession().data;
  const draft: CatalogDraft = detail.draft;
  const [working, setWorking] = useState<CatalogSnapshot>(draft.snapshot);
  const [reason, setReason] = useState(draft.reason);
  const [subjectId, setSubjectId] = useState(draft.snapshot.subjects[0]?.id ?? '');
  const [decision, setDecision] = useState<Decision | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  // Quando o rascunho no serviço muda (salvo, aprovado), a edição local recomeça dele.
  useEffect(() => {
    setWorking(draft.snapshot);
    setReason(draft.reason);
    setEditorKey((k) => k + 1);
  }, [draft.updated_at, draft.snapshot, draft.reason]);

  const readOnly = draft.status === 'publicado' || draft.status === 'descartado';
  const dirty = useMemo(() => JSON.stringify(working) !== JSON.stringify(draft.snapshot) || reason !== draft.reason, [working, draft, reason]);
  const validation = useLiveValidation(readOnly ? null : working);

  const save = useApiMutation('updateDraft', ['draft', 'drafts']);
  const publish = useApiMutation('publishDraft', ['draft', 'drafts', 'catalog', 'publications', 'overview']);
  const discard = useApiMutation('discardDraft', ['draft', 'drafts', 'overview']);

  function apply(s: Subject) {
    setWorking((w) => ({ ...w, subjects: w.subjects.map((x) => (x.id === subjectId ? s : x)) }));
    setSubjectId(s.id);
    toast.success(t('context.form.applied'));
  }

  function doSave() {
    save.mutate(
      { params: { id: draft.id }, body: { snapshot: working, reason: reason.trim(), expected_updated_at: draft.updated_at } },
      {
        onSuccess: () => toast.success(t('context.editor.savedDraft')),
        onError: (err) => toast.error(err instanceof ApiError && err.status === 409 ? t('errors.conflict') : errorMessage(t, err)),
      },
    );
  }

  const subject = working.subjects.find((s) => s.id === subjectId);
  return (
    <>
      <PageHeader
        title={t('context.editor.title', { id: draft.id.slice(0, 10) })}
        subtitle={draft.reason}
        actions={
          <>
            <StatusBadge tone={draftTone[draft.status]}>{t(`context.drafts.status.${draft.status}`)}</StatusBadge>
            {!readOnly ? (
              <Button variant="danger" onClick={() => setConfirmDiscard(true)}>
                {t('context.editor.discard')}
              </Button>
            ) : null}
            {session?.is_admin && !readOnly ? (
              <Button
                variant="primary"
                disabled={!detail.status.ready || dirty || publish.isPending}
                title={t('context.editor.publishHelp')}
                onClick={() =>
                  publish.mutate(
                    { params: { id: draft.id } },
                    { onSuccess: (p) => toast.success(t('context.editor.published', { id: p.id })), onError: (err) => toast.error(errorMessage(t, err)) },
                  )
                }
              >
                {t('context.editor.publish')}
              </Button>
            ) : null}
          </>
        }
      />
      <p className="-mt-3 mb-5">
        <Link to="/context" search={{ tab: 'drafts' }} className={`${linkClass} inline-flex items-center gap-1`}>
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t('context.editor.backToDrafts')}
        </Link>
      </p>
      {readOnly ? <Alert tone="info" title={t('context.editor.readOnly', { status: t(`context.drafts.status.${draft.status}`) })} className="mb-4" /> : null}

      <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
        <div className="flex min-w-0 flex-col gap-4">
          {!readOnly ? (
            <Card>
              <CardContent className="flex flex-col gap-3">
                <Field id="motivo-edicao" label={t('context.editor.reasonLabel')}>
                  <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
                </Field>
                <div className="flex flex-wrap items-center gap-3">
                  <Button variant="primary" onClick={doSave} disabled={!dirty || !validation.ok || validation.pending || save.isPending || !reason.trim()}>
                    {t('context.editor.save')}
                  </Button>
                  <span className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t('context.editor.saveHelp')}</span>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {working.subjects.length ? (
            <Tabs value={subjectId} onValueChange={setSubjectId}>
              <TabsList aria-label={t('context.editor.subject')}>
                {working.subjects.map((s) => (
                  <TabsTrigger key={s.id} value={s.id}>
                    {s.nome}
                  </TabsTrigger>
                ))}
              </TabsList>
              {subject ? (
                <TabsContent value={subject.id}>
                  <SubjectEditor key={`${subject.id}-${editorKey}`} subject={subject} readOnly={readOnly} onApply={apply} />
                </TabsContent>
              ) : null}
            </Tabs>
          ) : null}
        </div>

        <div className="flex flex-col gap-4">
          <ApprovalsCard detail={detail} onDecide={setDecision} />
          <Card>
            <CardHeader>
              <CardTitle>{t('context.editor.changes')}</CardTitle>
            </CardHeader>
            <CardContent>
              {draft.changes.length ? (
                <ul className="list-disc pl-4">
                  {draft.changes.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-fg-secondary">{t('context.editor.noChanges')}</p>
              )}
            </CardContent>
          </Card>
          {!readOnly ? (
            <Card>
              <CardHeader>
                <CardTitle>{t('context.editor.validation')}</CardTitle>
              </CardHeader>
              <CardContent aria-live="polite">
                {validation.pending ? (
                  <p className="text-fg-secondary">{t('context.editor.validating')}</p>
                ) : validation.ok ? (
                  <StatusBadge tone="success">{t('context.editor.valid')}</StatusBadge>
                ) : (
                  <Alert tone="danger" title={t('context.editor.invalid')}>
                    <ul className="list-disc pl-4 font-mono text-[length:var(--lr-text-xs)]">
                      {validation.errors.slice(0, 20).map((e) => (
                        <li key={e}>{e}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      {decision ? (
        <DecisionDialog
          draftId={draft.id}
          d={decision}
          subjectName={draft.snapshot.subjects.find((s) => s.id === decision.subject_id)?.nome ?? decision.subject_id}
          onClose={() => setDecision(null)}
        />
      ) : null}
      {confirmDiscard ? (
        <Dialog open onOpenChange={(o) => !o && setConfirmDiscard(false)}>
          <DialogContent title={t('context.editor.discard')} description={t('context.editor.discardConfirm')} closeLabel={t('common.close')}>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setConfirmDiscard(false)}>{t('common.cancel')}</Button>
              <Button
                variant="danger"
                onClick={() =>
                  discard.mutate(
                    { params: { id: draft.id } },
                    {
                      onSuccess: () => {
                        toast.success(t('context.editor.discarded'));
                        setConfirmDiscard(false);
                      },
                      onError: (err) => toast.error(errorMessage(t, err)),
                    },
                  )
                }
              >
                {t('context.editor.discard')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

export function DraftEditorPage({ draftId }: { draftId: string }) {
  const q = useApiQuery('draft', { params: { id: draftId } });
  return <QueryState query={q}>{(detail) => <Editor detail={detail} />}</QueryState>;
}
